import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { findBlurRelatedTarget } from './DomUtil';
import { ElemAttrs } from './HtmlAttrs';
import { FocusBlue } from '../styles/FocusIndicators';
type NestedCSSProperties = TypeStyle.types.NestedCSSProperties;

export interface FocusIndicatorProps {
  /**
   * This component requires a single child which accepts the DOM attributes noted in the typedef
   */
  children: React.ReactElement<Pick<ElemAttrs, 'className' | 'onMouseDownCapture' | 'onKeyDownCapture' | 'onBlurCapture'>>;
  /**
   * When true, don't hide the focus indicator for pointer interactions
   */
  showForPointer?: boolean;
}

export const InitialFocusVisibleContext = React.createContext<boolean>(true);
InitialFocusVisibleContext.displayName = 'InitialFocusVisibleContext';

export type FocusIndicatorState = {
  focusVisible: boolean;
};

export const FocusVisibleClassName = 'focus-visible';

const MakeFocusIndicatorStyles = (color: string = FocusBlue, inset: boolean = false) => Object.freeze<NestedCSSProperties>({
  borderColor: color,
  boxShadow: inset ? `inset 0 0 0 1px ${color}` : `0 0 4px 0 ${color}`,
});

const StandardIndicatorStyleName = TypeStyle.style({
  $nest: {
    '&:focus, & :focus': { outline: 'none', },
    '&::-moz-focus-inner, & ::-moz-focus-inner': { $unique: true, border: 'none' },
    '&.focus-visible:focus': MakeFocusIndicatorStyles(FocusBlue),
    '&.focus-visible :focus': MakeFocusIndicatorStyles(FocusBlue),
  },
});

/** @private This is an experimental component which may be broken or removed without notice.
 * If you believe you have a need for it, please contact #dev-shared-widgets so we may explore your use case together.
 */
// tslint:disable-next-line:class-name
export class __UNSTABLE__INTERNAL__FocusIndicator extends React.PureComponent<FocusIndicatorProps, FocusIndicatorState> {
  public context!: React.ContextType<typeof InitialFocusVisibleContext>;
  public state: FocusIndicatorState = { focusVisible: this.context };

  private handleBlur: React.FocusEventHandler<HTMLElement> = e => {
    const { currentTarget } = e;
    const focusTarget = findBlurRelatedTarget(e);
    this.setState(prevState => ({ focusVisible: prevState.focusVisible || !currentTarget.contains(focusTarget) }));
    this.props.children.props.onBlurCapture?.(e);
  }

  private handleKeyDown: React.KeyboardEventHandler<HTMLElement> = e => {
    /* Nav keys we care about:
     * 9 Tab
     * 27 Esc
     * 33 PgUp
     * 34 PgDn
     * 35 End
     * 36 Home
     * 37 Left
     * 38 Up
     * 39 Right
     * 40 Down
     */
    const { keyCode, shiftKey } = e;
    if (shiftKey || keyCode === /*tab*/ 9 || keyCode === /*esc*/ 27 || (keyCode >= 33 && keyCode <= 40)) {
      this.setState({ focusVisible: true });
    }
    this.props.children.props.onKeyDownCapture?.(e);
  }

  private handleMouseDown: React.MouseEventHandler<HTMLElement> = e => {
    this.setState({ focusVisible: !!this.props.showForPointer });
    this.props.children.props.onMouseDownCapture?.(e);
  }

  public render(): React.ReactNode {
    const { children, showForPointer, ...rest } = this.props;
    return React.cloneElement(React.Children.only(children), {
      ...rest,
      className: TypeStyle.classes(children.props.className, StandardIndicatorStyleName, {
        [FocusVisibleClassName]: this.state.focusVisible,
      }),
      onKeyDownCapture: this.handleKeyDown,
      // TODO does this need PointerEventWrapper to work in VizClient? ugh hope not
      onMouseDownCapture: this.handleMouseDown,
      onBlurCapture: this.handleBlur,
    });
  }
}
__UNSTABLE__INTERNAL__FocusIndicator.contextType = InitialFocusVisibleContext;
